import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { CollapsibleSection } from "components/collapsibleSection";
import { Label } from "components/label";
import Input from "components/input";
import { SwitchButton } from "components/switchButton";
import Select from "components/select";
import { useEvent } from "routes/ewaiver/hooks/useEvent";
import { useTranslation } from "@refinedev/core";
import useWindowWidth from "utills/InitWidthDevice";
import {
  PaperClipIcon,
  PencilIcon,
  PlusCircleIcon,
} from "@heroicons/react/outline";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DateTimeFormatPicker from "components/dateTimeFormatPicker";
import FilesDropzone from "routes/organization/org/components/filesDropzone";
import { DotsVerticalIcon, TrashIcon, XIcon } from "@heroicons/react/solid";
import { useLocation, useNavigate } from "react-router-dom";
import BannerDropzone from "../BannerDropzone";
import ImportHtmlButton from "../ImportHtmlButton";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { DropdownMenu } from "components/dropdownMenu";
import { get } from "lodash";
import RadioButton from "components/radioButton";
import toast from "react-hot-toast";
import ImagesDropzone from "../ImagesDropzone";
import { generateUUID } from "utills/helpers";

dayjs.extend(customParseFormat);

interface PropType {
  defaultValues: any | null;
  loading: boolean;
  onFormSubmit: (data: any) => void;
}

const EwaiverForm = (props: PropType) => {
  const { defaultValues, loading, onFormSubmit } = props;

  const navigate = useNavigate();
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const windowWidth = useWindowWidth();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(false);
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [tempEwaiverHtmlUrl, setTempEwaiverHtmlUrl] = useState<string>(
    defaultValues?.html_url ?? ""
  );

  const isEdit = location.pathname.includes("edit") ? true : false;

  const { data: events } = useEvent({
    initQuery: {
      use_create: location.pathname.includes("edit") ? "" : "true",
    },
  });

  const EVENT_OPTIONS = useMemo(() => {
    return (
      events
        ?.filter((e) => !(e?.event_name_vi === "" && e?.event_name === ""))
        ?.map((e) => {
          const name =
            e?.event_name_vi !== "" ? e?.event_name_vi : e?.event_name;

          return {
            label: name,
            value: e?.event_id ?? "",
          };
        }) ?? []
    );
  }, [events]);

  console.log({ events: events, eventsOption: EVENT_OPTIONS });

  // Zod schema
  const FormSchema = z
    .object({
      // is_active: z.boolean().refine((val) => val === true, {
      //   message: "Status is required",
      // }),
      is_active: z.boolean(),
      event_id: z.string().min(1, "Event name is required"),

      start_date_time: z.string().min(1, "Start date time is required"),
      end_date_time: z.string().min(1, "End date time is required"),
      authorization_form_url: z.string(),
      disclaimer_form_url: z.string(),
      t_and_c_link: z.string().min(1, "T&C Link is required"),

      banner_url: z.string(),

      // html_url: z.string(),
      // html_url: z
      //   .instanceof(File)
      //   .refine((file) => file.type === "text/html", {
      //     message: "Chỉ hỗ trợ file .html",
      //   })
      //   .optional(),
      html_url: z
        .union([
          z.instanceof(File).refine((file) => file.type === "text/html", {
            message: "Just support .html format",
          }),
          z.string(),
        ])
        .optional(),

      // review_ewaiver_file: z.boolean(),
      ewaiver_signature_required: z.boolean(),
      send_email_to_authorizer: z.boolean(),
      send_bib_via_email: z.boolean(),
      is_show_send_ewaiver: z.boolean(),
      is_show_send_qrcode: z.boolean(),
      change_pickup_method: z.boolean(),
      max_pickup_method: z.number().nullable(),
      change_authorized_info: z.boolean(),
      max_authorized_info: z.number().nullable(),
      authorize_only: z.boolean(),

      event_infos: z.array(
        z.object({
          title_name_vi: z.string().min(1, "Title name vi is required"),
          title_name_en: z.string().min(1, "Title name en is required"),
          status: z.boolean(),
          type: z.enum(["url", "images"]),
          url: z.string().min(1, "URL field is required").optional(),
          images: z
            .array(z.string().url("URL is not valid"))
            .min(1, "This Images field is required")
            .optional(),
        })
      ),
      // .min(1, "Phải có ít nhất 1 thông tin sự kiện")
      // .max(5, "Tối đa 5 thông tin sự kiện"),

      use_ekyc: z.boolean(),
      ekyc_orc_limit: z.number().nullable(),
      ekyc_face_matching_limit: z.number().nullable(),
      ekyc_face_matching_ratio: z.number().nullable(),
    })
    .superRefine((data, ctx) => {
      // Kiểm tra start_date_time < end_date_time
      const start = dayjs(data.start_date_time, "DD/MM/YYYY HH:mm", true);
      const end = dayjs(data.end_date_time, "DD/MM/YYYY HH:mm", true);

      if (start.isValid() && end.isValid() && !end.isAfter(start)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End Date Time must after Start Date Time",
          path: ["end_date_time"],
        });
      }

      // Kiểm tra change_pickup_method
      if (data.change_pickup_method) {
        if (data.max_pickup_method === null || data.max_pickup_method < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field must be greater than or equal to 1",
            path: ["max_pickup_method"],
          });
        }
      }

      // Kiểm tra change_authorized_info
      if (data.change_authorized_info) {
        if (data.max_authorized_info === null || data.max_authorized_info < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field must be greater than or equal to 1",
            path: ["max_authorized_info"],
          });
        }
      }

      // Kiểm tra event_info type
      data.event_infos.forEach((info, index) => {
        if (info.type === "url") {
          if (!info.url || info.url.trim() === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "URl is required",
              path: ["event_infos", index, "url"],
            });
          }
        }

        if (info.type === "images") {
          if (!info.images || info.images.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Images is required",
              path: ["event_infos", index, "images"],
            });
          } else {
            info.images.forEach((img, imgIndex) => {
              if (!/^https?:\/\//.test(img)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Url is not valid",
                  path: ["event_infos", index, "images", imgIndex],
                });
              }
            });
          }
        }
      });

      // Kiểm tra use_ekyc
      if (data.use_ekyc) {
        if (data.ekyc_orc_limit === null || data.ekyc_orc_limit < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field must be greater than or equal to 1",
            path: ["ekyc_orc_limit"],
          });
        }

        if (
          data.ekyc_face_matching_limit === null ||
          data.ekyc_face_matching_limit < 1
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field must be greater than or equal to 1",
            path: ["ekyc_face_matching_limit"],
          });
        }

        if (
          data.ekyc_face_matching_ratio === null ||
          data.ekyc_face_matching_ratio < 1
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field must be greater than or equal to 1",
            path: ["ekyc_face_matching_ratio"],
          });
        }
      }
    });

  type FormType = z.infer<typeof FormSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
    watch,
    setFocus,
    clearErrors,
  } = useForm<FormType>({
    resolver: zodResolver(FormSchema),
    defaultValues: defaultValues ?? {
      is_active: true,
      event_id: "",

      start_date_time: "",
      end_date_time: "",
      authorization_form_url: "",
      disclaimer_form_url: "",
      t_and_c_link: "",

      banner_url: "",

      // html_url: "",
      html_url: undefined,
      // review_ewaiver_file: true,
      ewaiver_signature_required: false,
      send_email_to_authorizer: false,
      send_bib_via_email: false,
      is_show_send_ewaiver: false,
      is_show_send_qrcode: false,
      change_pickup_method: false,
      max_pickup_method: 1,
      change_authorized_info: false,
      max_authorized_info: 1,
      authorize_only: false,

      event_infos: [],

      use_ekyc: false,
      ekyc_orc_limit: 0,
      ekyc_face_matching_limit: 0,
      ekyc_face_matching_ratio: 0,
    },
  });

  const { fields, append, remove, move, replace } = useFieldArray({
    control,
    name: "event_infos",
  });

  const eventInfos = watch("event_infos");
  const bannerUrl = watch("banner_url");
  const changePickupMethod = watch("change_pickup_method");
  const htmlUrl = watch("html_url");
  const changeAuthorizedInfo = watch("change_authorized_info");
  const useEkyc = watch("use_ekyc");
  const ewaiverSignRequired = watch("ewaiver_signature_required");

  // Check eWaiver file is FILE or STRING
  const isHtmlFile = htmlUrl instanceof File;
  const isHtmlString = typeof htmlUrl === "string";

  const handleBack = () => {
    navigate("/ewaiver");
  };

  const handleDrag = ({ source, destination }: any) => {
    if (destination) {
      move(source.index, destination.index);
    }
  };

  const collectMessages = (obj: any) => {
    const messages: string[] = [];

    const recurse = (value: any) => {
      if (Array.isArray(value)) {
        value.forEach(recurse);
      } else if (typeof value === "object" && value !== null) {
        if ("message" in value && typeof value.message === "string") {
          messages.push(value.message);
        }
        for (const key in value) {
          recurse(value[key]);
        }
      }
    };

    recurse(obj);
    return messages;
  };

  const onSubmit = (data: FormType) => {
    console.log("✅ Dữ liệu hợp lệ:", data);

    onFormSubmit(data);

    // Reset form when create new
    if (!isEdit) reset();
  };

  const onError = (errors: any) => {
    console.error("Lỗi submit:", errors);

    const firstErrorKey = Object.keys(errors)[0];
    setFocus(firstErrorKey as any);

    // const errorMessges = collectMessages(errors);

    // errorMessges.forEach((msg: string) => {
    //   toast.error(msg ?? "Submit form failed");
    // });
  };

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <form
      className="w-full flex flex-col gap-5 mb-[100px]"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <CollapsibleSection label="eWaiver Information" defaultOpen={true}>
        <div className="p-6 flex flex-col gap-5">
          <div
            className={`w-full flex gap-x-2 gap-y-5 ${
              isMobile ? "flex-col" : ""
            }`}
          >
            {/* Status */}
            <Controller
              control={control}
              name="is_active"
              render={({ field, fieldState }) => (
                <div
                  className={`flex flex-col gap-1 ${
                    isMobile ? "w-[100%]" : "w-[20%]"
                  }`}
                >
                  <Label required id="status" name="Status" />

                  <div className="h-[46px]">
                    <SwitchButton
                      checked={field.value}
                      onChange={() => {
                        setValue("is_active", !field.value);
                      }}
                    />
                  </div>

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* Event Name */}
            <Controller
              name="event_id"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div
                    className={`flex flex-col gap-1 ${
                      isMobile ? "w-[100%]" : "w-[80%]"
                    }`}
                  >
                    <Label id={"event_id"} name={"Event Name"} required />

                    <Select
                      isDisabled={isEdit}
                      error={fieldState.error}
                      options={EVENT_OPTIONS}
                      id="event_id"
                      value={EVENT_OPTIONS.filter((item) =>
                        field.value.includes(item.value)
                      )}
                      placeholder="Select Event"
                      onChange={(selected: {
                        label: string;
                        value: string;
                      }) => {
                        setValue("event_id", selected.value);
                        clearErrors("event_id");
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />
          </div>

          <div className={`w-full flex gap-5 ${isMobile ? "flex-col" : ""}`}>
            {/* Start date time*/}
            <Controller
              name="start_date_time"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div
                    className={`flex flex-col gap-1 ${
                      isMobile ? "w-[50%]" : "w-[20%]"
                    }`}
                  >
                    <Label
                      id={"start_date_time"}
                      name={"Start Date Time"}
                      required
                    />

                    <DateTimeFormatPicker
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="Start Date"
                      showSeconds={false}
                      ref={(e) => {
                        e && field.ref(e.input);
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Ene date time*/}
            <Controller
              name="end_date_time"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div
                    className={`flex flex-col gap-1 ${
                      isMobile ? "w-[50%]" : "w-[20%]"
                    }`}
                  >
                    <Label
                      id={"end_date_time"}
                      name={"End Date Time"}
                      required
                    />

                    <DateTimeFormatPicker
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="End Date"
                      showSeconds={false}
                      ref={(e) => {
                        e && field.ref(e.input);
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Authorization Form URL */}
            <Controller
              control={control}
              name="authorization_form_url"
              render={({ field, fieldState }) => (
                <div
                  className={`flex flex-col gap-1 ${
                    isMobile ? "w-[100%]" : "w-[30%]"
                  }`}
                >
                  <Label
                    required={false}
                    id="authorization_form_url"
                    name="Authorization Form URL"
                  />

                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Authorization Form URL"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* Disclaimer Form URL */}
            <Controller
              control={control}
              name="disclaimer_form_url"
              render={({ field, fieldState }) => (
                <div
                  className={`flex flex-col gap-1 ${
                    isMobile ? "w-[100%]" : "w-[30%]"
                  }`}
                >
                  <Label
                    required={false}
                    id="disclaimer_form_url"
                    name="Disclaimer Form URL"
                  />

                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Disclaimer Form URL"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          {/* T&C Link */}
          <Controller
            control={control}
            name="t_and_c_link"
            render={({ field, fieldState }) => (
              <div
                className={`flex flex-col gap-1 ${
                  isMobile ? "w-[100%]" : "w-[50%]"
                }`}
              >
                <Label required id="t_and_c_link" name="T&C Link" />

                <Input
                  {...field}
                  type="text"
                  error={fieldState.error}
                  placeholder="T&C Link"
                  className="bg-white"
                />

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <div className="w-full">
            {/* eWaiver Banner */}
            <Controller
              name="banner_url"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className={`w-full flex flex-col gap-1`}>
                    <Label
                      id={"banner_url"}
                      name={"eWaiver Banner"}
                      required={false}
                    />

                    {bannerUrl !== "" ? (
                      <div
                        className={`relative w-full group ${
                          isMobile ? "h-[136px]" : "h-[490px]"
                        }`}
                      >
                        <img
                          src={bannerUrl}
                          className="w-full h-full object-fit"
                          alt="banner_url"
                        />
                        {/* Overlay đen khi hover */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Nút xóa ở giữa */}
                        <button
                          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                                      text-white p-4 rounded-full shadow border-2 border-white 
                                      opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          onClick={() => {
                            console.log("Remove banner URL");

                            setValue("banner_url", "");
                          }}
                        >
                          <XIcon className="text-white size-[24px]" />
                        </button>
                      </div>
                    ) : (
                      <BannerDropzone
                        id="banner_url"
                        className={`${isMobile ? "h-[136px]" : "h-[490px]"}`}
                        isMobile={isMobile}
                        error={fieldState.error}
                        value={field}
                        onChange={(url: string) => {
                          setValue("banner_url", url);
                        }}
                      />
                    )}

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />
          </div>

          <div
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-3"
            }`}
          >
            {/* html_url */}
            <Controller
              name="html_url"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className={`w-full flex flex-col gap-1`}>
                    <Label
                      id={"html_url"}
                      name={"Import HTML"}
                      required={false}
                    />

                    <div className="flex flex-col gap-[8px] items-start">
                      <div className={`w-[250px]`}>
                        <ImportHtmlButton
                          id="html_url"
                          error={fieldState.error}
                          value={field}
                          onChange={(data: { url: string; file: File }) => {
                            console.log("html_url", data);
                            setValue("html_url", data.file);
                            setTempEwaiverHtmlUrl(data.url);
                          }}
                        />
                      </div>

                      {/* When update eWaiver is URL string */}
                      {isHtmlString && htmlUrl !== "" && (
                        <div
                          className="px-[12px] py-[8px] rounded-[30px] bg-[#E9ECFF]
                                        flex items-center gap-[8px]"
                        >
                          <PaperClipIcon className="size-[20px]" />

                          <a
                            href={tempEwaiverHtmlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group w-[144px] hover:cursor-pointer flex"
                          >
                            <label
                              className="w-full max-w-full truncate text-[12px] font-[500]
                                          group-hover:cursor-pointer group-hover:underline"
                            >
                              {htmlUrl}
                            </label>
                          </a>

                          <XIcon
                            className="size-[20px] hover:cursor-pointer"
                            onClick={() => {
                              console.log("Remove html_url string:", htmlUrl);
                              setValue("html_url", "");
                              setTempEwaiverHtmlUrl("");
                            }}
                          />
                        </div>
                      )}

                      {/* When create eWaiver is FILE */}
                      {isHtmlFile && htmlUrl && (
                        <div
                          className="px-[12px] py-[8px] rounded-[30px] bg-[#E9ECFF]
                                      flex items-center gap-[8px]"
                        >
                          <PaperClipIcon className="size-[20px]" />

                          <a
                            href={tempEwaiverHtmlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group w-[144px] hover:cursor-pointer flex"
                          >
                            <label
                              className="w-full max-w-full truncate text-[12px] font-[500]
                                          group-hover:cursor-pointer group-hover:underline"
                            >
                              {htmlUrl.name}
                            </label>
                          </a>

                          <XIcon
                            className="size-[20px] hover:cursor-pointer"
                            onClick={() => {
                              console.log("Remove html_url file:", htmlUrl);
                              setValue("html_url", undefined); // vì nó là File | undefined
                              setTempEwaiverHtmlUrl("");
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Review eWaiver File */}
            {/* <Controller
              control={control}
              name="review_ewaiver_file"
              render={({ field, fieldState }) => {
                return (
                  <div className={`flex flex-col gap-1`}>
                    <Label
                      required={false}
                      id="review_ewaiver_file"
                      name="Review eWaiver File"
                    />

                    <Input
                      type="checkbox"
                      id="review_ewaiver_file"
                      checked={field.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue("review_ewaiver_file", e.target.checked)
                      }
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            /> */}

            {!isMobile && (
              <>
                <div className="opacity-0">field</div>

                <div className="opacity-0">field</div>
              </>
            )}

            {/* eWaiver Signature Required */}
            <Controller
              control={control}
              name="ewaiver_signature_required"
              render={({ field, fieldState }) => {
                return (
                  <div className={`flex flex-col gap-1`}>
                    <div className="flex items-center gap-[8px]">
                      <Input
                        type="checkbox"
                        id="ewaiver_signature_required"
                        checked={field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked === true) {
                            setValue("authorize_only", false);
                          }

                          setValue(
                            "ewaiver_signature_required",
                            e.target.checked
                          );
                        }}
                      />

                      <div className="mt-2">
                        <Label
                          required={false}
                          id="ewaiver_signature_required"
                          name="eWaiver Signature Required"
                        />
                      </div>
                    </div>

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Send Email to Authorizer */}
            <Controller
              control={control}
              name="send_email_to_authorizer"
              render={({ field, fieldState }) => {
                return (
                  <div className={`flex flex-col gap-1`}>
                    <div className="flex items-center gap-[8px]">
                      <Input
                        type="checkbox"
                        id="send_email_to_authorizer"
                        checked={field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue("send_email_to_authorizer", e.target.checked)
                        }
                      />

                      <div className="mt-2">
                        <Label
                          required={false}
                          id="send_email_to_authorizer"
                          name="Send Email to Authorizer"
                        />
                      </div>
                    </div>

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Send BIB via Email */}
            <Controller
              control={control}
              name="send_bib_via_email"
              render={({ field, fieldState }) => {
                return (
                  <div className={`flex flex-col gap-1`}>
                    <div className="flex items-center gap-[8px]">
                      <Input
                        type="checkbox"
                        id="send_bib_via_email"
                        checked={field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue("send_bib_via_email", e.target.checked)
                        }
                      />

                      <div className="mt-2">
                        <Label
                          required={false}
                          id="send_bib_via_email"
                          name="Send BIB via Email"
                        />
                      </div>
                    </div>

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {([
              { name: "is_show_send_ewaiver", label: "Show Send eWaiver" },
              { name: "is_show_send_qrcode", label: "Show Send QR Code" },
            ] as const).map(({ name, label }) => (
              <Controller
                key={name}
                control={control}
                name={name}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-[8px]">
                      <Input
                        type="checkbox"
                        id={name}
                        checked={field.value ?? false}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                      />
                      <div className="mt-2">
                        <Label required={false} id={name} name={label} />
                      </div>
                    </div>
                    {fieldState.error && <span className="error-text font-medium">{fieldState.error.message}</span>}
                  </div>
                )}
              />
            ))}

            {/* Change Pick-up Method */}
            <Controller
              control={control}
              name="change_pickup_method"
              render={({ field, fieldState }) => {
                return (
                  <div className={`flex flex-col gap-1`}>
                    <div className="flex items-center gap-[8px]">
                      <Input
                        type="checkbox"
                        id="change_pickup_method"
                        checked={field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (!e.target.checked)
                            setValue("max_pickup_method", 1);

                          setValue("change_pickup_method", e.target.checked);
                        }}
                      />

                      <div className="mt-2">
                        <Label
                          required={false}
                          id="change_pickup_method"
                          name="Change Pick-up Method"
                        />
                      </div>
                    </div>

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Change Authorized Info */}
            <Controller
              control={control}
              name="change_authorized_info"
              render={({ field, fieldState }) => {
                return (
                  <div className={`flex flex-col gap-1`}>
                    <div className="flex items-center gap-[8px]">
                      <Input
                        type="checkbox"
                        id="change_authorized_info"
                        checked={field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (!e.target.checked)
                            setValue("max_authorized_info", 1);

                          setValue("change_authorized_info", e.target.checked);
                        }}
                      />

                      <div className="mt-2">
                        <Label
                          required={false}
                          id="change_authorized_info"
                          name="Change Authorized Info"
                        />
                      </div>
                    </div>

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* Authorize Only */}
            {!ewaiverSignRequired ? (
              <Controller
                control={control}
                name="authorize_only"
                render={({ field, fieldState }) => {
                  return (
                    <div className={`flex flex-col gap-1`}>
                      <div className="flex items-center gap-[8px]">
                        <Input
                          type="checkbox"
                          id="authorize_only"
                          checked={field.value}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setValue("authorize_only", e.target.checked);
                          }}
                        />

                        <div className="mt-2">
                          <Label
                            required={false}
                            id="authorize_only"
                            name="Authorize only"
                          />
                        </div>
                      </div>

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            ) : (
              <div className={`opacity-0 ${isMobile && "hidden"}`}>field</div>
            )}

            {/* Max Number of Times to Update Pick-up Method */}
            {changePickupMethod ? (
              <Controller
                control={control}
                name="max_pickup_method"
                render={({ field, fieldState }) => {
                  return (
                    <div
                      className={`flex flex-col gap-1 ${
                        isMobile ? "" : "ml-[23px]"
                      }`}
                    >
                      <Label
                        required
                        id="max_pickup_method"
                        name="Max Number of Times to Update Pick-up Method"
                      />

                      <Input
                        className={`${isMobile ? "w-full" : "w-[315px]"}`}
                        error={fieldState.error}
                        type="number"
                        id="max_pickup_method"
                        value={field.value}
                        min={1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue("max_pickup_method", Number(e.target.value))
                        }
                      />

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            ) : (
              <div className={`opacity-0 ${isMobile && "hidden"}`}>field</div>
            )}

            {/* Max Number of Times to Update Authorized Info */}
            {changeAuthorizedInfo ? (
              <Controller
                control={control}
                name="max_authorized_info"
                render={({ field, fieldState }) => {
                  return (
                    <div
                      className={`flex flex-col gap-1 ${
                        isMobile ? "" : "ml-[23px]"
                      }`}
                    >
                      <Label
                        required
                        id="max_authorized_info"
                        name="Max Number of Times to Update Authorized Info"
                      />

                      <Input
                        className={`${isMobile ? "w-full" : "w-[315px]"}`}
                        error={fieldState.error}
                        type="number"
                        id="max_authorized_info"
                        value={field.value}
                        min={1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue(
                            "max_authorized_info",
                            Number(e.target.value)
                          )
                        }
                      />

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            ) : (
              <div className="opacity-0">field</div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        label="Event Info In eWaiver Email"
        defaultOpen={true}
      >
        <div className="p-6">
          <div className="mb-[16px] flex items-center gap-5">
            <Button
              type="button"
              className={`${
                eventInfos?.length < 5
                  ? "bg-white hover:bg-white"
                  : "bg-white hover:bg-white"
              }`}
              style={{
                border: `1px solid ${
                  eventInfos?.length < 5 ? "#011BB6" : "#A8B3BD"
                }`,
              }}
              onClick={() => {
                if (eventInfos?.length < 5) {
                  append({
                    title_name_vi: "",
                    title_name_en: "",
                    status: true,
                    type: "url",
                    url: undefined,
                    images: undefined,
                  });
                }
              }}
            >
              {eventInfos?.length < 5 ? (
                <PlusCircleIcon className="size-[16px] text-[#011BB6] mr-2" />
              ) : (
                <PencilIcon className="size-[16px] text-[#A8B3BD] mr-2" />
              )}

              <span
                className={`text-[14px] font-[400] ${
                  eventInfos?.length < 5 ? "text-[#011BB6]" : "text-[#A8B3BD]"
                }`}
              >
                Add More
              </span>
            </Button>

            <div
              className="flex items-center justify-center
                          px-[25px] py-[10px] bg-[#E9EDF1] rounded-[20px]"
            >
              <span className="text-[14px] font-[400]">
                {fields.length} / 5
              </span>
            </div>
          </div>

          <DragDropContext onDragEnd={handleDrag}>
            <div className="grid sm:grid-cols-6 lg:gap-x-6">
              <Droppable droppableId="test-items">
                {(provided, snapshot) => (
                  <div
                    className="col-span-full"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {fields.map((field, index: number) => {
                      return (
                        <Draggable
                          key={`event_infos[${index}]`}
                          draggableId={`item-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              className={`border rounded-lg mb-4 ${
                                get(errors, `event_infos.${index}`, "")
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                              key={field.id}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <CollapsibleSection
                                label={`${
                                  get(
                                    watch("event_infos")[index],
                                    `title_name_en`
                                  ) === ""
                                    ? "Title Name"
                                    : get(
                                        watch("event_infos")[index],
                                        `title_name_en`
                                      )
                                }`}
                                customOpen={{
                                  open: openSectionId === field.id,
                                  onClick: () =>
                                    setOpenSectionId((prevId) =>
                                      prevId === field.id ? null : field.id
                                    ),
                                }}
                                isAnimated={false}
                                toolbar={
                                  <div className="flex items-center gap-[12px]">
                                    {get(
                                      watch("event_infos")[index],
                                      "status"
                                    ) ? (
                                      <div
                                        className="flex items-center justify-center bg-[#E9ECFF]
                                                    rounded-[100px] px-[12px] py-[4px]"
                                      >
                                        <span className="text-[12px] font-[500] text-[#011BB6]">
                                          Show
                                        </span>
                                      </div>
                                    ) : (
                                      <div
                                        className="flex items-center justify-center bg-[#E9EDF1]
                                                    rounded-[100px] px-[12px] py-[4px]"
                                      >
                                        <span className="text-[12px] font-[500] text-[#868E95]">
                                          Hide
                                        </span>
                                      </div>
                                    )}

                                    <DropdownMenu
                                      label={
                                        <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                                      }
                                      items={[
                                        ...(index !== 0
                                          ? [
                                              {
                                                label: "Move up",
                                                onClick: () =>
                                                  move(index, index - 1),
                                              },
                                            ]
                                          : []),
                                        ...(index !== fields.length - 1
                                          ? [
                                              {
                                                label: "Move Down",
                                                onClick: () =>
                                                  move(index, index + 1),
                                              },
                                            ]
                                          : []),
                                        {
                                          label: `${
                                            get(
                                              watch("event_infos")[index],
                                              "status"
                                            )
                                              ? "Hide"
                                              : "Show"
                                          }`,
                                          onClick: () => {
                                            const currentValue = get(
                                              watch("event_infos")[index],
                                              "status"
                                            );

                                            setValue(
                                              `event_infos.${index}.status`,
                                              !currentValue
                                            );
                                          },
                                        },
                                        {
                                          label: "Delete",
                                          onClick: () => remove(index),
                                        },
                                      ]}
                                    />
                                  </div>
                                }
                                headerProps={{ ...provided.dragHandleProps }}
                              >
                                <div className="p-6 flex flex-col gap-5">
                                  <div className="flex flex-col gap-[4px]">
                                    <Label
                                      id={"event_id"}
                                      name={"Event Name"}
                                      required
                                    />

                                    <Controller
                                      control={control}
                                      name={`event_infos.${index}.title_name_vi`}
                                      render={({ field, fieldState }) => (
                                        <div className="relative w-full">
                                          <Input
                                            {...field}
                                            id={`event_infos.${index}.title_name_vi`}
                                            type="text"
                                            placeholder="Title Name VI"
                                            className="pr-11"
                                            error={fieldState.error}
                                          />
                                          <img
                                            src={`/images/flags/circle-vi.png`}
                                            alt="Language Icon"
                                            className="absolute top-1/2 right-3 -translate-y-1/2"
                                          />
                                        </div>
                                      )}
                                    />

                                    <Controller
                                      control={control}
                                      name={`event_infos.${index}.title_name_en`}
                                      render={({ field, fieldState }) => (
                                        <div className="relative w-full">
                                          <Input
                                            {...field}
                                            id={`event_infos.${index}.title_name_en`}
                                            type="text"
                                            placeholder="Title Name EN"
                                            className="pr-11"
                                            error={fieldState.error}
                                          />
                                          <img
                                            src={`/images/flags/circle-en.png`}
                                            alt="Language Icon"
                                            className="absolute top-1/2 right-3 -translate-y-1/2"
                                          />
                                        </div>
                                      )}
                                    />
                                  </div>

                                  <Controller
                                    control={control}
                                    name={`event_infos.${index}.type`}
                                    render={({ field, fieldState }) => {
                                      return (
                                        <div className="flex items-center gap-[37px]">
                                          <RadioButton
                                            checked={
                                              get(
                                                watch("event_infos")[index],
                                                `type`
                                              ) === "url"
                                            }
                                            onChange={(e) => {
                                              setValue(
                                                `event_infos.${index}.type`,
                                                "url"
                                              );

                                              // Reset value
                                              setValue(
                                                `event_infos.${index}.images`,
                                                undefined
                                              );
                                            }}
                                            item={{
                                              id: "url",
                                              title: "URL",
                                            }}
                                            key={`event_infos.${index}.type.url`}
                                          />

                                          <RadioButton
                                            checked={
                                              get(
                                                watch("event_infos")[index],
                                                `type`
                                              ) === "images"
                                            }
                                            onChange={() => {
                                              setValue(
                                                `event_infos.${index}.type`,
                                                "images"
                                              );

                                              // Reset value
                                              setValue(
                                                `event_infos.${index}.url`,
                                                undefined
                                              );
                                            }}
                                            item={{
                                              id: "images",
                                              title: "Images",
                                            }}
                                            key={`event_infos.${index}.type.images`}
                                          />
                                        </div>
                                      );
                                    }}
                                  />

                                  {get(watch("event_infos")[index], `type`) ===
                                    "url" && (
                                    <Controller
                                      control={control}
                                      name={`event_infos.${index}.url`}
                                      render={({ field, fieldState }) => (
                                        <div
                                          className={`flex flex-col gap-1 w-[100%]`}
                                        >
                                          <Label
                                            required
                                            id={`event_infos.${index}.url`}
                                            name="URL"
                                          />

                                          <Input
                                            {...field}
                                            type="text"
                                            error={fieldState.error}
                                            placeholder="Input"
                                            className="bg-white"
                                          />

                                          {fieldState.error && (
                                            <span className="error-text font-medium">
                                              {fieldState.error.message}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    />
                                  )}

                                  {get(watch("event_infos")[index], `type`) ===
                                    "images" && (
                                    <Controller
                                      control={control}
                                      name={`event_infos.${index}.images`}
                                      render={({ field, fieldState }) => (
                                        <div
                                          className={`flex flex-col gap-[10px]`}
                                        >
                                          <Label
                                            required
                                            id={`event_infos.${index}.images`}
                                            name="Image"
                                          />

                                          <div className="h-auto flex items-center gap-[10px] flex-wrap">
                                            {get(
                                              watch("event_infos")[index],
                                              "images"
                                            )?.map(
                                              (img: string, idx: number) => {
                                                return (
                                                  <div
                                                    key={generateUUID()}
                                                    className="relative group w-[45%] md:w-[200px] h-[85px] rounded-lg"
                                                  >
                                                    <img
                                                      className="w-full h-full object-cover rounded-lg"
                                                      src={img}
                                                      alt="image"
                                                    />

                                                    {/* Overlay đen khi hover */}
                                                    <div
                                                      className="absolute inset-0 bg-black/50 opacity-0
                                                          group-hover:opacity-100 transition-opacity duration-300"
                                                    />

                                                    {/* Nút xóa ở giữa */}
                                                    <div
                                                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                                                            text-white p-[12px] rounded-[8px] bg-[#A8B3BD] hover:cursor-pointer
                                                            opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                      onClick={() => {
                                                        console.log(
                                                          `Remove image URL index: ${idx}`
                                                        );

                                                        const filterImages =
                                                          get(
                                                            watch(
                                                              "event_infos"
                                                            )[index],
                                                            "images"
                                                          )?.filter(
                                                            (img, i) => {
                                                              return i !== idx;
                                                            }
                                                          );

                                                        setValue(
                                                          `event_infos.${index}.images`,
                                                          filterImages
                                                        );
                                                      }}
                                                    >
                                                      <TrashIcon className="text-white size-[24px]" />
                                                    </div>
                                                  </div>
                                                );
                                              }
                                            )}

                                            <div className="w-[45%] md:w-[200px] h-[85px]">
                                              <ImagesDropzone
                                                value={get(
                                                  watch("event_infos")[index],
                                                  "images"
                                                )}
                                                onChange={(urls: string[]) => {
                                                  console.log(
                                                    "Upload images:",
                                                    urls
                                                  );

                                                  const currentImages =
                                                    watch(
                                                      `event_infos.${index}.images`
                                                    ) ?? [];

                                                  const mergedImages = [
                                                    ...currentImages,
                                                    ...urls,
                                                  ];

                                                  setValue(
                                                    `event_infos.${index}.images`,
                                                    mergedImages
                                                  );
                                                }}
                                              />
                                            </div>
                                          </div>

                                          <p className="mt-1 text-sm text-[#868E95]">
                                            Maximum file size: 3MB{" "}
                                            <span className="mx-2">•</span>{" "}
                                            Supported images file: PNG, JPEG,
                                            JPG
                                          </p>

                                          {fieldState.error && (
                                            <span className="error-text font-medium">
                                              {fieldState.error.message}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    />
                                  )}
                                </div>
                              </CollapsibleSection>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>

          {typeof errors.event_infos?.message === "string" && (
            <span className="error-text font-medium">
              {errors.event_infos.message}
            </span>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection label="eKYC" defaultOpen={false}>
        <div className="p-6 flex flex-col gap-5">
          {/* Use eKYC */}
          <Controller
            control={control}
            name="use_ekyc"
            render={({ field, fieldState }) => {
              return (
                <div className={`flex flex-col gap-1`}>
                  <Label required={false} id="use_ekyc" name="Use eKYC" />

                  <Input
                    type="checkbox"
                    id="use_ekyc"
                    checked={field.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.checked) {
                        setValue("ekyc_orc_limit", 0);
                        setValue("ekyc_face_matching_limit", 0);
                        setValue("ekyc_face_matching_ratio", 0);
                      }

                      if (e.target.checked) {
                        setValue("ekyc_orc_limit", 1);
                        setValue("ekyc_face_matching_limit", 1);
                        setValue("ekyc_face_matching_ratio", 80);
                      }

                      setValue("use_ekyc", e.target.checked);
                    }}
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              );
            }}
          />

          {useEkyc && (
            <div
              className={`grid gap-5 ${
                isMobile ? "grid-cols-1" : "grid-cols-3"
              }`}
            >
              {/* eKyc Orc Limit */}
              <Controller
                control={control}
                name="ekyc_orc_limit"
                render={({ field, fieldState }) => {
                  return (
                    <div className={`flex flex-col gap-1`}>
                      <Label
                        required
                        id="ekyc_orc_limit"
                        name="eKyc Orc Limit"
                      />

                      <Input
                        error={fieldState.error}
                        type="number"
                        id="ekyc_orc_limit"
                        value={field.value}
                        min={1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue("ekyc_orc_limit", Number(e.target.value))
                        }
                      />

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />

              {/* eKyc Face Matching Limit */}
              <Controller
                control={control}
                name="ekyc_face_matching_limit"
                render={({ field, fieldState }) => {
                  return (
                    <div className={`flex flex-col gap-1`}>
                      <Label
                        required
                        id="ekyc_face_matching_limit"
                        name="eKyc Face Matching Limit"
                      />

                      <Input
                        error={fieldState.error}
                        type="number"
                        id="ekyc_face_matching_limit"
                        value={field.value}
                        min={1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue(
                            "ekyc_face_matching_limit",
                            Number(e.target.value)
                          )
                        }
                      />

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />

              {/* eKyc Orc Limit */}
              <Controller
                control={control}
                name="ekyc_face_matching_ratio"
                render={({ field, fieldState }) => {
                  return (
                    <div className={`flex flex-col gap-1`}>
                      <Label
                        required
                        id="ekyc_face_matching_ratio"
                        name="eKyc Face Matching Ratio"
                      />

                      <Input
                        error={fieldState.error}
                        type="number"
                        id="ekyc_face_matching_ratio"
                        value={field.value}
                        min={1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setValue(
                            "ekyc_face_matching_ratio",
                            Number(e.target.value)
                          )
                        }
                      />

                      {fieldState.error && (
                        <span className="error-text font-medium">
                          {fieldState.error.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>
      </CollapsibleSection>

      <div className="fixed bottom-0 left-0 right-0 bg-white p-[16px] flex gap-3 items-center justify-end">
        <button
          type="button"
          className="w-[88px] h-[48px] flex items-center justify-center
                    border-[1px] border-[#E9ECFF] rounded-[4px]
                    text-[#011BB6] text-[14px] cursor-pointer"
          onClick={handleBack}
        >
          Cancel
        </button>

        <button
          disabled={loading}
          type="submit"
          className={`w-[88px] h-[48px] flex items-center justify-center
              border-[1px] border-[#011BB6] bg-[#011BB6] rounded-[4px]
              text-white text-[14px]
              ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          {loading ? "Loading..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export default EwaiverForm;
